import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  addDays,
  diff,
  fmtLong,
  freeRun,
  key,
  monthLabel,
  parse,
  spanFree,
  todayKey,
} from './dates.js';

const WEEKDAYS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

const GRID_GAP = 44; // must match .shs-calendar-grids / .shs-calendar-track column gap
const SLIDE_MS = 280;
const MAX_SHIFT = 23; // 24-month booking horizon; availability lazy-loads behind the view
const SLIDE_EASE = 'cubic-bezier(.25,.8,.25,1)';
const PREFETCH_AHEAD = 4; // request data this many months past the furthest visible one

function monthIndex(d) {
  return d.getFullYear() * 12 + d.getMonth();
}

export default function AvailabilityCalendar({
  title = 'Select dates',
  minNights = 1,
  minNightsByDate = {},
  blocked = [],
  checkIn = '',
  checkOut = '',
  today = todayKey(),
  loadedUntil = '',
  onNeedMonths,
  doneLabel = 'Done',
  onChange,
  onDone,
  // Optional extra gate on check-in dates (aggregate calendars: a free night
  // isn't necessarily one where any single home can start a whole stay).
  startAllowed,
  startBlockedText = 'No home free long enough',
}) {
  // Anchor to today's month and never move it on selection - the two-month
  // view must stay put when a check-in is picked in the right-hand month.
  const [shift, setShift] = useState(() => {
    if (!checkIn) return 0;
    return Math.max(0, monthIndex(parse(checkIn)) - monthIndex(parse(today)));
  });
  const [focusDate, setFocusDate] = useState(checkIn || today);
  const [tip, setTip] = useState(null); // { date, text }
  // Slide animation state: { from, to, monthW, step, phase, prevHeight, targetH }.
  const [motion, setMotion] = useState(null);
  // Phones get a vertical month scroll instead of the two-month slide view.
  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 640px)').matches);
  // Bottom-most visible month offset, tracked while scrolling on mobile.
  const [visibleBottom, setVisibleBottom] = useState(1);
  const gridRef = useRef(null);
  const viewportRef = useRef(null);
  const trackRef = useRef(null);
  const scrollRaf = useRef(0);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    const onMq = () => setIsMobile(mq.matches);
    mq.addEventListener('change', onMq);
    return () => mq.removeEventListener('change', onMq);
  }, []);

  useEffect(() => () => cancelAnimationFrame(scrollRaf.current), []);

  const blockedSet = useMemo(() => new Set(blocked), [blocked]);
  const minFor = useCallback((d) => minNightsByDate[d] ?? minNights, [minNightsByDate, minNights]);

  // Month navigation with a sliding transition on desktop. A click mid-slide
  // re-targets the animation from the track's live pixel position, so rapid
  // clicks chain smoothly through many months instead of locking. Mobile
  // navigates by scrolling, so this only ever runs on desktop.
  const goTo = useCallback(
    (nextRaw) => {
      const next = Math.max(0, Math.min(MAX_SHIFT, nextRaw));
      if (next === shift) return;
      if (isMobile) {
        setMotion(null);
        setShift(next);
        return;
      }
      const vp = viewportRef.current;
      const w = vp ? vp.clientWidth : 0;
      const monthW = (w - GRID_GAP) / 2;
      const step = monthW + GRID_GAP;
      // Current visual position in month-index units: exactly `shift` when
      // settled, measured from the live transform when mid-slide.
      let from = shift;
      if (motion && trackRef.current && motion.step) {
        const tf = window.getComputedStyle(trackRef.current).transform;
        const x = tf && tf !== 'none' ? new DOMMatrixReadOnly(tf).m41 : NaN;
        if (Number.isFinite(x)) {
          from = Math.floor(Math.min(motion.from, motion.to)) + -x / motion.step;
        }
      }
      setMotion({
        from,
        to: next,
        monthW,
        step,
        phase: 'start',
        prevHeight: vp ? vp.offsetHeight : null,
        targetH: null,
      });
      setShift(next);
    },
    [motion, shift, isMobile]
  );

  // Drive the slide: paint the track at the old position with no transition,
  // then on the next frame move it to the new position and animate the
  // viewport height to the incoming month pair. Clear when done.
  useEffect(() => {
    if (!motion) return;
    if (motion.phase === 'start') {
      let raf2;
      const raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => {
          const track = trackRef.current;
          let targetH = motion.prevHeight;
          if (track) {
            const min = Math.floor(Math.min(motion.from, motion.to));
            const a = track.children[motion.to - min];
            const b = track.children[motion.to - min + 1];
            if (a && b) targetH = Math.max(a.offsetHeight, b.offsetHeight);
          }
          setMotion((m) => (m && m.phase === 'start' ? { ...m, phase: 'animate', targetH } : m));
        });
      });
      return () => {
        cancelAnimationFrame(raf1);
        if (raf2) cancelAnimationFrame(raf2);
      };
    }
    const t = setTimeout(() => setMotion(null), SLIDE_MS + 40);
    return () => clearTimeout(t);
  }, [motion]);

  const mode = !checkIn || checkOut ? 'start' : 'end';

  const startOK = useCallback(
    (d) => {
      if (blockedSet.has(d) || d < today) return false;
      if (loadedUntil && d > loadedUntil) return false;
      if (startAllowed && !startAllowed(d)) return false;
      // Days past the loaded horizon are unknown, not free - cap the run there.
      const run = freeRun(d, blockedSet, 365);
      const capped = loadedUntil ? Math.min(run, diff(d, loadedUntil) + 1) : run;
      return capped >= minFor(d);
    },
    [blockedSet, minFor, today, loadedUntil, startAllowed]
  );

  // Aria/tooltip note for free dates a startAllowed gate rejects.
  const startNote = useCallback(
    (d) => (mode === 'start' && startAllowed && !startAllowed(d) ? startBlockedText : ''),
    [mode, startAllowed, startBlockedText]
  );

  const endOK = useCallback(
    (d) =>
      d > checkIn &&
      (!loadedUntil || d <= loadedUntil) &&
      spanFree(checkIn, d, blockedSet) &&
      diff(checkIn, d) >= minFor(checkIn),
    [checkIn, blockedSet, minFor, loadedUntil]
  );

  // Tooltip auto-dismiss
  useEffect(() => {
    if (!tip) return;
    const t = setTimeout(() => setTip(null), 2800);
    return () => clearTimeout(t);
  }, [tip]);

  const handleSelect = useCallback(
    (d) => {
      if (d < today || (loadedUntil && d > loadedUntil)) return;
      // Completing a range?
      if (mode === 'end' && endOK(d)) {
        onChange?.(checkIn, d);
        setTip(null);
        return;
      }
      // Otherwise treat the click as starting a new range.
      if (startOK(d)) {
        onChange?.(d, '');
        const n = minFor(d);
        setTip(n > 1 ? { date: d, text: n + '-night minimum' } : null);
        // If the earliest valid checkout falls beyond the visible months,
        // bring it into view - slide on desktop, smooth-scroll on mobile.
        const target = monthIndex(parse(addDays(d, n))) - monthIndex(parse(today));
        if (isMobile) {
          requestAnimationFrame(() => {
            const vp = viewportRef.current;
            const el = vp && vp.querySelector('[data-monthoffset="' + target + '"]');
            if (!vp || !el) return;
            const vr = vp.getBoundingClientRect();
            const er = el.getBoundingClientRect();
            if (er.bottom > vr.bottom || er.top < vr.top) {
              el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
          });
        } else {
          const over = target - (shift + 1);
          if (over > 0) goTo(shift + over);
        }
      } else if (blockedSet.has(d)) {
        const co = addDays(d, -1) >= today && !blockedSet.has(addDays(d, -1));
        setTip({ date: d, text: co ? 'Checkout only' : 'Booked' });
      } else if (mode === 'start' && startAllowed && !startAllowed(d)) {
        setTip({ date: d, text: startBlockedText });
      } else {
        setTip({ date: d, text: minFor(d) + '-night minimum' });
      }
    },
    [mode, checkIn, endOK, startOK, blockedSet, minFor, today, loadedUntil, onChange, shift, goTo, isMobile, startAllowed, startBlockedText]
  );

  const handleClear = useCallback(() => {
    setTip(null);
    onChange?.('', '');
  }, [onChange]);

  const monthA = useMemo(
    () => buildMonth(today, shift, 0, checkIn, checkOut, blockedSet, startOK, endOK, mode, minFor, handleSelect, tip, loadedUntil, startNote),
    [today, shift, checkIn, checkOut, blockedSet, startOK, endOK, mode, minFor, handleSelect, tip, loadedUntil, startNote]
  );
  const monthB = useMemo(
    () => buildMonth(today, shift, 1, checkIn, checkOut, blockedSet, startOK, endOK, mode, minFor, handleSelect, tip, loadedUntil, startNote),
    [today, shift, checkIn, checkOut, blockedSet, startOK, endOK, mode, minFor, handleSelect, tip, loadedUntil, startNote]
  );

  // While sliding, render every month from the old view's first to the new
  // view's last on one horizontal track. `from` can be fractional when a
  // click lands mid-slide, hence the floor/ceil.
  const trackMonths = useMemo(() => {
    if (!motion) return null;
    const min = Math.floor(Math.min(motion.from, motion.to));
    const max = Math.ceil(Math.max(motion.from, motion.to));
    const out = [];
    for (let off = min; off <= max + 1; off++) {
      out.push(buildMonth(today, off, 0, checkIn, checkOut, blockedSet, startOK, endOK, mode, minFor, handleSelect, tip, loadedUntil, startNote));
    }
    return out;
  }, [motion, today, checkIn, checkOut, blockedSet, startOK, endOK, mode, minFor, handleSelect, tip, loadedUntil, startNote]);

  // Mobile: every month in the booking horizon, in one vertical scroll list.
  const scrollMonths = useMemo(() => {
    if (!isMobile) return null;
    const out = [];
    for (let off = 0; off <= MAX_SHIFT + 1; off++) {
      out.push({ off, ...buildMonth(today, off, 0, checkIn, checkOut, blockedSet, startOK, endOK, mode, minFor, handleSelect, tip, loadedUntil, startNote) });
    }
    return out;
  }, [isMobile, today, checkIn, checkOut, blockedSet, startOK, endOK, mode, minFor, handleSelect, tip, loadedUntil, startNote]);

  // Lazy loading: keep the data horizon a few months past the furthest
  // visible month. Refires when loadedUntil advances in case more is needed.
  useEffect(() => {
    onNeedMonths?.((isMobile ? visibleBottom : shift + 1) + PREFETCH_AHEAD);
  }, [isMobile, visibleBottom, shift, loadedUntil, onNeedMonths]);

  // Mobile scroll: track the bottom-most visible month for prefetching.
  const handleScroll = useCallback(() => {
    if (scrollRaf.current) return;
    scrollRaf.current = requestAnimationFrame(() => {
      scrollRaf.current = 0;
      const vp = viewportRef.current;
      if (!vp) return;
      const vr = vp.getBoundingClientRect();
      let max = 0;
      vp.querySelectorAll('[data-monthoffset]').forEach((el) => {
        if (el.getBoundingClientRect().top < vr.bottom) {
          max = Math.max(max, Number(el.dataset.monthoffset));
        }
      });
      setVisibleBottom((prev) => (prev === max ? prev : max));
    });
  }, []);

  // Mobile: open scrolled to the month holding the current check-in.
  useEffect(() => {
    if (!isMobile || !checkIn) return;
    const vp = viewportRef.current;
    const target = monthIndex(parse(checkIn)) - monthIndex(parse(today));
    const el = vp && vp.querySelector('[data-monthoffset="' + target + '"]');
    if (el) vp.scrollTop = Math.max(0, el.offsetTop - 8);
    // Mount only - later selections never yank the scroll position.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const nights = checkIn && checkOut ? diff(checkIn, checkOut) : 0;

  const canPrev = shift > 0;

  const statusLine = useMemo(() => {
    if (nights > 0) {
      return nights + (nights === 1 ? ' night' : ' nights') + ' · ' + fmtLong(checkIn) + ' → ' + fmtLong(checkOut);
    }
    if (checkIn) {
      return 'Now choose your checkout date';
    }
    return '';
  }, [nights, checkIn, checkOut]);

  useEffect(() => {
    function onKeyDown(e) {
      if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter', ' '].includes(e.key)) return;
      e.preventDefault();
      const current = focusDate || checkIn || today;
      let next = current;
      if (e.key === 'ArrowUp') next = addDays(current, -7);
      else if (e.key === 'ArrowDown') next = addDays(current, 7);
      else if (e.key === 'ArrowLeft') next = addDays(current, -1);
      else if (e.key === 'ArrowRight') next = addDays(current, 1);
      else if (e.key === 'Enter' || e.key === ' ') {
        handleSelect(current);
        return;
      }
      setFocusDate(next);
      const btn = gridRef.current?.querySelector('[data-date="' + next + '"]');
      if (btn) btn.focus();
    }

    const node = gridRef.current;
    if (!node) return;
    node.addEventListener('keydown', onKeyDown);
    return () => node.removeEventListener('keydown', onKeyDown);
    // The grids unmount during a slide - re-attach when motion clears.
  }, [focusDate, checkIn, today, handleSelect, motion]);

  function valueStyle(filled) {
    return {
      fontSize: '14px',
      fontWeight: filled ? 600 : 400,
      color: filled ? '#2B2118' : '#B6A794',
    };
  }

  // Viewport height is pinned during a slide and animated to the incoming
  // month pair's height, so 5-row vs 6-row months resize smoothly.
  let viewportStyle;
  if (motion) {
    viewportStyle = { overflow: 'hidden' };
    if (motion.phase === 'start' && motion.prevHeight != null) viewportStyle.height = motion.prevHeight;
    if (motion.phase === 'animate') {
      if (motion.targetH != null) viewportStyle.height = motion.targetH;
      viewportStyle.transition = 'height ' + SLIDE_MS + 'ms ' + SLIDE_EASE;
    }
  }

  let trackStyle;
  if (motion) {
    const min = Math.floor(Math.min(motion.from, motion.to));
    const offset = motion.phase === 'animate' ? motion.to - min : motion.from - min;
    trackStyle = { transform: 'translateX(' + (-offset * motion.step) + 'px)' };
    if (motion.phase === 'animate') trackStyle.transition = 'transform ' + SLIDE_MS + 'ms ' + SLIDE_EASE;
  }

  return (
    <div className="shs-calendar-card" role="dialog" aria-modal="true" aria-label={title}>
      <div className="shs-calendar-header">
        <div>
          <div className="shs-calendar-title">{title}</div>
          <div className="shs-calendar-status" aria-live="polite">{statusLine}</div>
        </div>
        <div className="shs-calendar-readout">
          <div className="shs-calendar-readout-cell">
            <span className="shs-calendar-readout-label">Check-in</span>
            <span style={valueStyle(!!checkIn)}>{checkIn ? fmtLong(checkIn) : 'Add date'}</span>
          </div>
          {checkIn && !checkOut && (
            <button
              type="button"
              className="shs-calendar-clear"
              onClick={handleClear}
              aria-label="Clear dates"
              title="Clear dates"
            >
              ×
            </button>
          )}
          <div
            className="shs-calendar-readout-cell shs-calendar-readout-checkout"
            data-active={mode === 'end' && !checkOut}
          >
            <span className="shs-calendar-readout-label">Checkout</span>
            <span style={valueStyle(!!checkOut)}>{checkOut ? fmtLong(checkOut) : 'Add date'}</span>
          </div>
        </div>
      </div>

      <div
        className="shs-calendar-viewport"
        ref={viewportRef}
        style={viewportStyle}
        onScroll={isMobile ? handleScroll : undefined}
      >
        {!isMobile && (
          <div className="shs-calendar-nav">
            <button
              type="button"
              className="shs-calendar-nav-btn"
              onClick={() => goTo(shift - 1)}
              disabled={!canPrev}
              aria-label="Previous month"
            >
              ‹
            </button>
            <button
              type="button"
              className="shs-calendar-nav-btn shs-calendar-nav-next"
              onClick={() => goTo(shift + 1)}
              disabled={shift >= MAX_SHIFT}
              aria-label="Next month"
            >
              ›
            </button>
          </div>
        )}

        {isMobile ? (
          <div className="shs-calendar-scroll" ref={gridRef}>
            {scrollMonths.map((m) => (
              <MonthGrid key={m.label} label={m.label} cells={m.cells} monthOffset={m.off} />
            ))}
          </div>
        ) : motion && trackMonths ? (
          <div className="shs-calendar-track" ref={trackRef} style={trackStyle}>
            {trackMonths.map((m) => (
              <MonthGrid key={m.label} label={m.label} cells={m.cells} width={motion.monthW} />
            ))}
          </div>
        ) : (
          <div className="shs-calendar-grids" ref={gridRef}>
            <MonthGrid label={monthA.label} cells={monthA.cells} />
            <MonthGrid label={monthB.label} cells={monthB.cells} />
          </div>
        )}
      </div>

      <div className="shs-calendar-footer">
        <div className="shs-calendar-legend">
          <span className="shs-legend-unavailable">Unavailable</span>
          <span className="shs-legend-selected">Your stay</span>
        </div>
        <div className="shs-calendar-actions">
          <button type="button" className="shs-calendar-clear-link" onClick={handleClear}>
            Clear dates
          </button>
          <button
            type="button"
            className="shs-calendar-done"
            onClick={onDone}
          >
            {nights > 0 ? doneLabel : 'Close'}
          </button>
        </div>
      </div>
    </div>
  );
}

function MonthGrid({ label, cells, width, monthOffset }) {
  return (
    <div className="shs-calendar-month" style={width ? { width } : undefined} data-monthoffset={monthOffset}>
      <div className="shs-calendar-month-label">{label}</div>
      <div className="shs-calendar-weekdays">
        {WEEKDAYS.map((d, i) => (
          <span key={i}>{d}</span>
        ))}
      </div>
      <div className="shs-calendar-days">
        {cells.map((c, i) =>
          c.empty ? (
            <div key={i} className="shs-calendar-day shs-calendar-day-empty" />
          ) : (
            <button
              key={c.date}
              type="button"
              data-date={c.date}
              className={
                'shs-calendar-day' +
                (c.isIn ? ' shs-calendar-day-in' : '') +
                (c.isOut ? ' shs-calendar-day-out' : '') +
                (c.inRange ? ' shs-calendar-day-range' : '') +
                (c.crossed ? ' shs-calendar-day-blocked' : '') +
                (c.muted ? ' shs-calendar-day-muted' : '') +
                (c.minstay ? ' shs-calendar-day-minstay' : '') +
                (c.pending ? ' shs-calendar-day-pending' : '') +
                (c.hasRange ? ' shs-calendar-day-has-range' : '')
              }
              onClick={c.onClick}
              aria-label={c.ariaLabel}
              aria-disabled={c.ariaDisabled}
              tabIndex={-1}
            >
              {c.label}
              {c.tip && <span className="shs-calendar-tip" role="status">{c.tip}</span>}
            </button>
          )
        )}
      </div>
    </div>
  );
}

function buildMonth(today, shift, offset, checkIn, checkOut, blockedSet, startOK, endOK, mode, minFor, onSelect, tip, loadedUntil, startNote) {
  const t = parse(today);
  const base = new Date(t.getFullYear(), t.getMonth() + shift + offset, 1);
  const y = base.getFullYear();
  const m = base.getMonth();
  const lead = (new Date(y, m, 1).getDay() + 6) % 7;
  const dim = new Date(y, m + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < lead; i++) {
    cells.push({ empty: true });
  }
  for (let d = 1; d <= dim; d++) {
    const k = key(new Date(y, m, d));
    const isIn = k === checkIn;
    const isOut = k === checkOut;
    const inRange = !!(checkIn && checkOut && k > checkIn && k < checkOut);
    const isPast = k < today;
    const isBlocked = blockedSet.has(k);
    // Past the loaded data window: unknown, never to be mistaken for free.
    const isPending = !!loadedUntil && k > loadedUntil;
    const prevK = key(new Date(y, m, d - 1));

    // A booked night whose previous night is free is the checkout morning of
    // an open stretch - guests can still depart that day, so it is never
    // crossed out. Crossed out means strictly "nothing to do here": past
    // dates, or booked nights inside a booked run. Never mode-dependent.
    const checkoutOnly = isBlocked && prevK >= today && !blockedSet.has(prevK);
    const crossed = !isIn && !isOut && (isPast || (isBlocked && !checkoutOnly));

    let selectable;
    if (isPast || isPending) selectable = false;
    else if (mode === 'end') selectable = endOK(k);
    else selectable = startOK(k);

    // Free nights that fall short of the chosen check-in's minimum stay get
    // a mid-tone - visibly "almost available", between muted and selectable.
    const minstayShort =
      !isPast && !isPending && !isIn && !isOut && !selectable &&
      mode === 'end' && !!checkIn && k > checkIn &&
      spanFree(checkIn, k, blockedSet) && diff(checkIn, k) < minFor(checkIn);

    // Muted (grey, no strikethrough) only while choosing a checkout date -
    // greying there guides the user to valid checkouts. Before anything is
    // selected, every future non-booked date looks normal; clicking one that
    // can't start a stay shows the tooltip explaining why.
    const muted = mode === 'end' && !isPast && !isPending && !crossed && !selectable && !isIn && !isOut && !minstayShort;

    let stateText = '';
    if (isPast) stateText = ', past';
    else if (isPending) stateText = ', availability not loaded yet';
    else if (isBlocked) stateText = checkoutOnly ? ', booked - checkout only' : ', booked';
    else if (minstayShort) stateText = ', ' + minFor(checkIn) + '-night minimum stay';
    else if (!selectable && !isIn && !isOut) {
      const note = startNote ? startNote(k) : '';
      stateText = note ? ', ' + note.toLowerCase() : ', ' + minFor(k) + '-night minimum stay';
    }

    cells.push({
      empty: false,
      date: k,
      label: String(d),
      isIn,
      isOut,
      inRange,
      crossed,
      muted,
      minstay: minstayShort,
      pending: isPending,
      hasRange: (isIn || isOut) && !!(checkIn && checkOut),
      ariaDisabled: !selectable && !isIn && !isOut,
      ariaLabel: fmtLong(k) + stateText,
      onClick: isPast || isPending ? null : () => onSelect(k),
      tip: tip && tip.date === k ? tip.text : null,
    });
  }
  return { label: monthLabel(y, m), cells };
}
